import {
  asArray,
  isObj,
  mapImage,
  mapText,
  mapTitle,
  normalizeSpacer,
} from "@/components/DesignSystem/utils/teaserHelper"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"
import { handleLearnMoreClick } from "@/components/DesignSystem/analytics/compareSliderAnalytics"
import { sanitizeTextForAnalytics } from "@/utils/helper"
import { Image } from "@kohler-dsm-library/kohler-core"

/**
 * Maps a "Compare Slider V2" AEM container into props consumable by the
 * `CompareSlider` design-system component.
 *
 * The AEM node uses a flat data shape where fields like `heading`, `cards`,
 * `cta`, `ctaLabel`, `topSpacer`, and `bottomSpacer` live directly on the
 * child node (no `basic` / `card` wrappers). Each card carries flat fields:
 *
 * - `featureicon` (image src), `alt`, `s7PresetType`, `imageModifiers`
 * - `title` (string, may contain HTML)
 * - `description` (string)
 * - `price` (string)
 * - `href`, `isNewTab`, `text` (card-level CTA)
 * - `features[]` with `{ text, featureicon, featureAltText, ... }`
 *
 * @param {Object} container - The AEM container object with `:items`,
 *   `:itemsOrder`, and component metadata (`dsComponentType` / `containerId`).
 * @return {Object|null} Mapped props shaped for the `CompareSlider` component,
 *   or `null` when the input is falsy, has no valid child node, or yields no cards.
 */
export function mapCompareSliderV2Props(container) {
  try {
    if (!container || !isObj(container)) return null

    // Resolve the child node from :items, or treat the container itself as the node
    const firstKey =
      asArray(container[":itemsOrder"])?.[0] ||
      (container[":items"] ? Object.keys(container[":items"])[0] : undefined)

    const node = firstKey ? container[":items"]?.[firstKey] : container
    if (!node || !isObj(node)) return null

    // --- Heading ---
    const heading = mapTitle(node.heading) || { text: "" }

    // --- Top-level CTA (flat: ctaLabel + cta + isNewTab) ---
    let CTA
    if (node.ctaLabel) {
      CTA = {
        text: node.ctaLabel,
        href: typeof node.cta === "string" ? modifyHref(node.cta) : "",
        target: node.isNewTab ? "_blank" : "_self",
      }
    }

    // --- Spacers (normalize to { size } objects) ---
    const topSpacer = node.topSpacer
      ? normalizeSpacer(
          isObj(node.topSpacer) ? node.topSpacer.size : node.topSpacer
        )
      : undefined
    const bottomSpacer = node.bottomSpacer
      ? normalizeSpacer(
          isObj(node.bottomSpacer) ? node.bottomSpacer.size : node.bottomSpacer
        )
      : undefined

    // --- Cards ---
    const cardsArr = asArray(node.cards)
      .map(cardItem => {
        if (!cardItem || !isObj(cardItem)) return null

        // Image from flat featureicon / alt / s7PresetType / imageModifiers
        const image = mapImage({
          src: cardItem.featureicon,
          alt: cardItem.alt ?? "",
          s7PresetType: cardItem.s7PresetType,
          imageModifiers: cardItem.imageModifiers,
        })
        if (!image) return null

        // Title (plain string, may contain HTML)
        const title = mapTitle(cardItem.title) || { text: "" }

        // Description & price (plain strings)
        const description = mapText(cardItem.description)
        const rawPrice = mapText(cardItem.price)
        const price = rawPrice
          ? { ...rawPrice, text: `$${rawPrice.text}` }
          : undefined

        // Card CTA from flat href / text / isNewTab
        let cardCTA
        if (cardItem.href) {
          cardCTA = {
            text: cardItem.ctaText || "",
            href: modifyHref(cardItem.href),
            target: cardItem.isNewTab ? "_blank" : "_self",
            onClick: () =>
              handleLearnMoreClick(
                sanitizeTextForAnalytics(cardItem.ctaText || "")
              ),
          }
        }

        // Features array
        const features = asArray(cardItem.features)
          .map(f => {
            if (!f || !isObj(f)) return null
            const featureProps = mapText(f)
            if (!featureProps) return null
            if (f.featureicon) {
              featureProps.icon = (
                <Image
                  src={f.featureicon}
                  width={20}
                  height={20}
                  alt={f.featureAltText ?? ""}
                  reveal="default"
                  loading="blank"
                />
              )
            }
            return featureProps
          })
          .filter(Boolean)

        return {
          image,
          title,
          ...(description ? { description } : {}),
          ...(price ? { price } : {}),
          ...(features.length ? { features } : {}),
          ...(cardCTA ? { CTA: cardCTA } : {}),
        }
      })
      .filter(Boolean)

    if (!cardsArr.length) return null

    const result = {
      heading,
      cards: cardsArr,
      ...(CTA ? { CTA } : {}),
      ...(topSpacer ? { topSpacer } : {}),
      ...(bottomSpacer ? { bottomSpacer } : {}),
    }

    return result
  } catch (error) {
    console.error("Error in mapCompareSliderV2Props:", error)
    return null
  }
}

export default mapCompareSliderV2Props
